import React from "react";
import { Link } from "react-router-dom";
import {
  ScanLine, Globe2, Receipt, Landmark, BarChart3, ShieldCheck, Percent,
  ArrowRight, ExternalLink, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { IMAGES } from "../mock";

const features = [
  { icon: ScanLine, title: "AI Bill Scanning", desc: "Snap or upload a supplier bill and AI vision reads the vendor, dates, line items and totals — then opens a pre-filled draft ready to save in one click." },
  { icon: Globe2, title: "Multi-Currency Done Right", desc: "Invoice and pay in any currency. Live FX rates auto-fill, stay overridable, and lock on save so reports roll up cleanly in your base currency." },
  { icon: Receipt, title: "Invoices That Get Paid", desc: "Branded templates, recurring schedules with auto-email, one-click payment receipts and a clean paid-invoice history." },
  { icon: Landmark, title: "Banking, Reconciled", desc: "Multiple accounts, CSV statement imports with auto-categorisation, duplicate detection and a focused reconciliation queue." },
  { icon: BarChart3, title: "Reports Accountants Love", desc: "P&L, Balance Sheet, Cash Flow, Aged AR/AP and Trial Balance — computed on base-currency totals, exportable as CSV." },
  { icon: ShieldCheck, title: "Real Security Built In", desc: "Role-based access for owners, admins, accountants and auditors, mandatory email 2FA, trusted devices and a signed audit log." },
];

const caymanPoints = [
  "Tax-exempt accounting mode, on by default for Cayman companies",
  "Zero-tax invoices with optional per-line overrides",
  "Reports never force VAT or sales tax logic in no-tax mode",
  "Multi-currency support: KYD, USD, GBP, EUR and more",
  "Full audit trail on every tax and jurisdiction change",
];

export default function BlueLedger() {
  return (
    <div data-testid="blueledger-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F2FAFB] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Badge className="bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] font-semibold px-4 py-1.5 rounded-full mb-6">
              Our Product • Accounting Software
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0A2540] tracking-tight leading-[1.1]">
              Blue Ledger — Accounting That Feels Like an <span className="text-[#0E9AAA]">Island Breeze</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Born in the Cayman Islands and built for the world, Blue Ledger is our modern bookkeeping platform for freelancers, founders and finance teams — with first-class Cayman jurisdiction support, a no-tax mode, and an interface you'll actually enjoy opening every morning.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="https://blueledger.ws" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8 h-12 shadow-lg hover:shadow-xl transition-shadow">
                  Visit blueledger.ws <ExternalLink size={17} className="ml-2" />
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white font-bold rounded-lg px-8 h-12 transition-colors">
                  Ask Us About It
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-8">
              <div><div className="text-2xl font-extrabold text-[#0A2540]">0%</div><div className="text-xs text-slate-500 font-semibold mt-1">Cayman corporate tax</div></div>
              <div><div className="text-2xl font-extrabold text-[#0A2540]">$0</div><div className="text-xs text-slate-500 font-semibold mt-1">to start with the Free plan</div></div>
              <div><div className="text-2xl font-extrabold text-[#0A2540]">5 min</div><div className="text-xs text-slate-500 font-semibold mt-1">to your first invoice</div></div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-40 h-40 bg-[#0E9AAA]/10 rounded-full blur-2xl" />
            <img src={IMAGES.accounting} alt="Blue Ledger accounting dashboard" className="rounded-2xl shadow-2xl w-full h-[400px] object-cover" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">Everything You Need</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tight">A Complete Back Office, Distilled</h2>
            <p className="mt-4 text-slate-600">Sales, purchases, banking, accounting and reporting — orchestrated in one calm interface.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {features.map((f) => (
              <Card key={f.title} className="rounded-2xl border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-xl bg-[#E8F7F9] flex items-center justify-center mb-5">
                    <f.icon className="text-[#0E9AAA]" size={24} />
                  </div>
                  <h3 className="font-extrabold text-[#0A2540] text-lg">{f.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Cayman jurisdiction */}
      <section className="py-20 bg-[#F6FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
              <Percent size={16} /> Cayman Jurisdiction Support
            </div>
            <h2 className="text-3xl font-extrabold text-[#0A2540] tracking-tight">No Corporate Income Tax. No Friction.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Blue Ledger ships with the Cayman Islands as a first-class jurisdiction. Your invoices, reports and reconciliation flows respect local reality from day one — and gracefully support VAT or sales-tax markets when you expand.
            </p>
            <ul className="mt-7 space-y-3.5">
              {caymanPoints.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-700 text-sm">
                  <CheckCircle2 size={18} className="text-[#0E9AAA] mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <img src={IMAGES.cayman} alt="Cayman Islands ocean" className="rounded-2xl shadow-xl w-full h-96 object-cover" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-[#0A2540] rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#0E9AAA]/10 rounded-full blur-3xl" />
            <h2 className="relative text-3xl font-extrabold text-white tracking-tight">Open a Clean Set of Books Today</h2>
            <p className="relative mt-4 text-slate-300">Start free. Upgrade when your business does. Your data always stays yours.</p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-4">
              <a href="https://blueledger.ws/register" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8 shadow-lg">
                  Start Free <ArrowRight size={17} className="ml-2" />
                </Button>
              </a>
              <a href="https://blueledger.ws/pricing" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#0A2540] font-bold rounded-lg px-8 transition-colors">
                  Compare Plans
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
